import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { Loader2, Trash2, X } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { ToneBadge } from '../../../../../components/ui/ToneBadge';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { inputClass } from '../../../../../components/ui/Field';
import {
  addPackageSupply,
  removePackageSupply,
  savePackage,
} from '../../../../../features/fulfillment/fulfillmentThunks';
import {
  LOGISTICS_FIELDS,
  availableForLine,
  boxFilledNotice,
  changedLogistics,
  changedProducts,
  isPackageComplete,
  logisticsFormFrom,
  packageProductsForm,
  packageSupplyProblem,
  packageTareGrams,
  resolveSupplyCode,
  supplyOptionLabel,
  unweighedNote,
  volumetricKg,
  weightSuggestionNote,
} from '../../../../../features/fulfillment/fulfillmentUtils';
import { formatQuantity } from '../../../../../utils/inventoryLabels';

/**
 * One parcel: what it was built with, what goes inside, and what it weighs
 * and measures — in that order, which is the order of the work.
 *
 * **Supplies.** The box, the tape, the film. Each one is picked from the
 * warehouse's supplies — or **scanned by its code**, which has to match whole
 * — and the server takes it out of the supplies ledger with a reference to
 * this order, so it can be costed and charged. Taking one off puts it back.
 *
 * **Contents.** Units of each picked line that travel in this parcel. The
 * ceiling of each field is what was picked minus what the other parcels
 * already claim: the same unit cannot travel in two boxes. The OMS's order
 * detail reads the same assignment.
 *
 * **Weight and dimensions.** Saved when the field is left, not with a button:
 * four numbers per parcel, and with ten parcels that would be forty trips to a
 * button. The weight is suggested by the server from the box's tare and the
 * products' weights, and it fills the field only while the field is empty or
 * still holds the previous suggestion; a number typed here is respected, and
 * "Usar" is how to take the suggestion back. The border says whether the
 * parcel is closed, which is the only question that matters before
 * dispatching — a parcel without weight and dimensions cannot be rated by the
 * courier, and the exit is refused naming it.
 *
 * @param {object} props
 * @param {object} props.pkg - The parcel as the server has it.
 * @param {number} props.orderId
 * @param {Array<object>} props.lines - The order's picklist lines.
 * @param {Array<object>} props.packages - Every parcel of the shipment, for the ceilings.
 * @param {Array<object>} props.supplyOptions - Active supplies of the order's warehouse.
 * @param {boolean} props.canRemove - Never the last one; the server refuses that.
 * @param {boolean} props.disabled
 * @param {(packageId: number) => void} props.onRemove
 * @param {(notice: {type: string, message: string}) => void} props.onDone
 */
export const PackageCard = ({
  pkg,
  orderId,
  lines = [],
  packages = [],
  supplyOptions = [],
  canRemove,
  disabled,
  onRemove,
  onDone,
}) => {
  const dispatch = useDispatch();
  const isBusy = useSelector((state) => state.fulfillment.busyPackageId === pkg.id);

  const [values, setValues] = useState(() => logisticsFormFrom(pkg));
  const [quantities, setQuantities] = useState(() => packageProductsForm(pkg));
  const [problem, setProblem] = useState(null);

  /** A supply picked from the list. Typing a code lets go of it. */
  const [pickedSupplyId, setPickedSupplyId] = useState(null);
  const [typedCode, setTypedCode] = useState('');
  const [supplyQuantity, setSupplyQuantity] = useState('1');

  // A reload brings the parcel back with what was saved; what is being typed
  // in another field is kept by merging only the fields that are empty here.
  // The weight is the exception: the server moves it to follow its suggestion,
  // and that move has to show.
  useEffect(() => {
    setValues((current) => {
      const next = { ...current };
      LOGISTICS_FIELDS.forEach(({ key }) => {
        if ((current[key] === '' || key === 'weight_grams') && pkg[key]) next[key] = pkg[key];
      });
      return next;
    });
    setQuantities(packageProductsForm(pkg));
  }, [pkg]);

  const complete = isPackageComplete(values);
  const volumetric = volumetricKg(values);

  const save = async (changed, onSaved) => {
    if (isBusy) return;
    setProblem(null);
    const action = await dispatch(savePackage({ orderId, packageId: pkg.id, values: changed }));
    if (action.meta.requestStatus !== 'fulfilled') {
      setProblem(action.payload);
      return;
    }
    if (onSaved) onSaved(action.payload);
    if (action.payload.delivery_status_changed) {
      onDone({ type: 'success', message: 'Todos los bultos quedaron cerrados: el pedido está armado.' });
    }
  };

  const commit = () => {
    const changed = changedLogistics(pkg, values);
    if (changed) save(changed);
  };

  const commitProducts = () => {
    const products = changedProducts(pkg, quantities, lines);
    if (products) save({ products });
  };

  const useSuggestion = () => {
    const total = pkg.weight_suggestion?.total;
    if (!total) return;
    setValues((current) => ({ ...current, weight_grams: total }));
    save({ weight_grams: total });
  };

  const picked = supplyOptions.find((supply) => supply.id === pickedSupplyId) || null;
  const supply = typedCode.trim() ? resolveSupplyCode(typedCode, supplyOptions) : picked;
  const supplyProblem = packageSupplyProblem({ supply, quantity: supplyQuantity, typed: typedCode });
  const supplyChoices = supplyOptions.map((item) => ({
    value: item.id,
    label: supplyOptionLabel(item),
    shortLabel: item.sku,
  }));

  const handleAddSupply = async () => {
    if (supplyProblem || isBusy) return;
    setProblem(null);
    const action = await dispatch(
      addPackageSupply({ orderId, packageId: pkg.id, supplyId: supply.id, quantity: supplyQuantity })
    );
    if (action.meta.requestStatus !== 'fulfilled') {
      setProblem(action.payload);
      return;
    }
    setTypedCode('');
    setSupplyQuantity('1');
    // A box lends the parcel its numbers; the server says which ones it set,
    // and the reload above brings them into the empty fields.
    const filled = boxFilledNotice(supply, action.payload.filled_from_box || []);
    onDone({
      type: 'success',
      message: [
        `${supply.name} ×${formatQuantity(supplyQuantity)} usado en el bulto ${pkg.item_index}.`,
        filled,
      ]
        .filter(Boolean)
        .join(' '),
    });
  };

  const tareGrams = packageTareGrams(pkg);
  const suggestion = weightSuggestionNote(pkg);
  const unweighed = unweighedNote(pkg);
  const suggestedTotal = pkg.weight_suggestion?.total || 0;
  const offerSuggestion = suggestedTotal > 0 && Number(values.weight_grams || 0) !== suggestedTotal;

  const handleRemoveSupply = async (row) => {
    if (isBusy) return;
    setProblem(null);
    const action = await dispatch(
      removePackageSupply({ orderId, packageId: pkg.id, packageSupplyId: row.id })
    );
    if (action.meta.requestStatus !== 'fulfilled') {
      setProblem(action.payload);
      return;
    }
    onDone({ type: 'success', message: `${row.name} devuelto a Insumos` });
  };

  const pickedLines = lines.filter((line) => Number(line.prepared_quantity) > 0);

  return (
    <div
      data-testid="package-card"
      data-package-id={pkg.id}
      data-complete={complete ? 'true' : 'false'}
      className={clsx(
        'rounded-2xl border bg-white dark:bg-[#1a1a1a] p-4 space-y-3',
        complete ? 'border-ninja-green' : 'border-border'
      )}
    >
      <div className="flex items-center gap-3">
        <span className="font-bold text-ninja-dark dark:text-white">Bulto {pkg.item_index}</span>
        <ToneBadge tone={complete ? 'success' : 'warning'} testId="package-state">
          {complete ? 'Cerrado' : 'Incompleto'}
        </ToneBadge>
        {pkg.tracking_number && (
          <span className="font-mono text-xs text-text-muted truncate">{pkg.tracking_number}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {isBusy && <Loader2 size={14} className="animate-spin text-text-muted" />}
          {canRemove && (
            <Button
              variant="subtle"
              size="sm"
              iconOnly
              icon={Trash2}
              aria-label={`Quitar el bulto ${pkg.item_index}`}
              onClick={() => onRemove(pkg.id)}
              disabled={disabled || isBusy}
              data-testid="package-remove"
            />
          )}
        </div>
      </div>

      {problem && (
        <div
          data-testid="package-error"
          className="bg-red-50 dark:bg-red-900/20 text-danger p-2.5 rounded-xl border border-red-200 dark:border-red-900 text-xs"
        >
          {problem}
        </div>
      )}

      {/* What the parcel was built with. */}
      <div data-testid="package-supplies" className="space-y-2">
        <span className="block text-xs font-semibold text-text-muted">Insumos usados</span>

        {(pkg.supplies || []).length === 0 ? (
          <p className="text-xs text-text-muted">
            Sin insumos todavía. Anota la caja y lo que usaste para armarlo: se descuenta de
            Insumos y se cobra al cliente.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {pkg.supplies.map((row) => (
              <li
                key={row.id}
                data-testid="package-supply-row"
                className="inline-flex items-center gap-1.5 rounded-full bg-ninja-gray dark:bg-[#2d2d2d] pl-3 pr-1.5 py-1 text-xs text-ninja-dark dark:text-white"
              >
                <span className="font-mono">{row.sku}</span>
                <span className="text-text-muted">{row.name}</span>
                <span className="font-semibold">×{formatQuantity(row.quantity)}</span>
                {!disabled && (
                  <Button
                    variant="subtle"
                    size="xs"
                    iconOnly
                    icon={X}
                    pill
                    aria-label={`Quitar ${row.name} del bulto ${pkg.item_index}`}
                    data-testid="package-supply-remove"
                    onClick={() => handleRemoveSupply(row)}
                    disabled={isBusy}
                    className="hover:text-danger"
                  />
                )}
              </li>
            ))}
          </ul>
        )}

        {!disabled && supplyOptions.length === 0 && (
          <p data-testid="package-no-supplies" className="text-xs text-text-muted">
            Esta bodega no tiene insumos activos. Se crean en Insumos.
          </p>
        )}

        {!disabled && supplyOptions.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
            <FilterableSelect
              variant="form"
              testId="package-supply"
              // What is typed wins over what was picked, so the list shows it too.
              value={supply?.id ?? null}
              onChange={(supplyId) => {
                setPickedSupplyId(supplyId);
                setTypedCode('');
              }}
              options={supplyChoices}
              placeholder="Elegir insumo..."
              searchPlaceholder="Buscar por SKU o nombre..."
              disabled={isBusy}
            />
            <input
              type="text"
              data-testid="package-supply-code"
              value={typedCode}
              onChange={(event) => setTypedCode(event.target.value)}
              // A scanner ends its read with Enter: that is the confirmation.
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleAddSupply();
              }}
              placeholder="o escanea su código"
              aria-label="Código del insumo"
              autoComplete="off"
              disabled={isBusy}
              className={clsx(inputClass, 'sm:w-40')}
            />
            <input
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              data-testid="package-supply-quantity"
              value={supplyQuantity}
              onChange={(event) => setSupplyQuantity(event.target.value)}
              aria-label="Cantidad del insumo"
              disabled={isBusy}
              className={clsx(inputClass, 'sm:w-20')}
            />
            {/* Disabled, it says what is missing. */}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleAddSupply}
              disabled={Boolean(supplyProblem) || isBusy}
              data-testid="package-supply-add"
            >
              {supplyProblem || 'Usar'}
            </Button>
          </div>
        )}
      </div>

      {/* What goes inside: units of each picked line. */}
      {pickedLines.length > 0 && (
        <div data-testid="package-products" className="border-t border-border pt-3 space-y-2">
          <span className="block text-xs font-semibold text-text-muted">Productos del bulto</span>
          <ul className="divide-y divide-border rounded-xl border border-border">
            {pickedLines.map((line) => {
              const available = availableForLine(line, packages, pkg);
              const current = quantities[line.order_product_id] ?? '';
              const over = Number(current || 0) > available;
              return (
                <li
                  key={line.order_product_id}
                  data-testid="package-product-row"
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <span className="font-mono font-semibold text-ninja-dark dark:text-white">{line.sku}</span>
                    <span className="ml-2 text-text-muted truncate">{line.product_name}</span>
                    <span
                      className={clsx('block text-xs', over ? 'text-danger' : 'text-text-muted')}
                      data-testid="package-product-available"
                    >
                      {formatQuantity(available)} disp. / {formatQuantity(line.prepared_quantity)} pickeadas
                    </span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    max={available}
                    step="1"
                    inputMode="numeric"
                    data-testid={`package-product-${line.order_product_id}`}
                    aria-label={`Unidades de ${line.sku} en el bulto ${pkg.item_index}`}
                    value={current}
                    onChange={(event) =>
                      setQuantities((state) => ({ ...state, [line.order_product_id]: event.target.value }))
                    }
                    onBlur={commitProducts}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                    }}
                    disabled={disabled || isBusy}
                    className={clsx(inputClass, 'w-20 text-right', over && 'border-danger')}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Weight and the three dimensions. */}
      <div className="border-t border-border pt-3 space-y-2">
        <span className="block text-xs font-semibold text-text-muted">Peso y medidas</span>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {LOGISTICS_FIELDS.map((field) => (
            <label key={field.key} className="block">
              <span className="block text-xs font-semibold text-text-muted mb-1">
                {field.label} ({field.unit})
              </span>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                data-testid={`package-${field.key}`}
                value={values[field.key]}
                onChange={(event) =>
                  setValues((current) => ({ ...current, [field.key]: event.target.value }))
                }
                onBlur={commit}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                }}
                disabled={disabled}
                className={inputClass}
              />
            </label>
          ))}
        </div>

        {(volumetric !== null || tareGrams > 0 || suggestion) && (
          <p className="text-xs text-text-muted flex flex-wrap items-center gap-x-3 gap-y-1">
            {volumetric !== null && (
              <span title="Largo × ancho × alto ÷ 4000. Los couriers cobran por el mayor entre el peso real y éste.">
                Peso volumétrico ≈ {volumetric.toFixed(2)} kg
              </span>
            )}
            {tareGrams > 0 && (
              <span
                data-testid="package-tare"
                title="Lo que pesa la caja vacía. El peso del bulto es el total, caja incluida."
              >
                Incluye {tareGrams} g de caja
              </span>
            )}
            {suggestion && (
              <span data-testid="package-weight-suggestion" title="Tara de la caja más el peso del maestro de cada producto por sus unidades. El campo sigue libre.">
                {suggestion}
              </span>
            )}
            {suggestion && offerSuggestion && !disabled && (
              <Button
                variant="subtle"
                size="xs"
                onClick={useSuggestion}
                disabled={isBusy}
                data-testid="package-weight-use"
              >
                Usar {suggestedTotal} g
              </Button>
            )}
          </p>
        )}
        {unweighed && (
          <p data-testid="package-unweighed" className="text-xs text-warning">
            {unweighed}
          </p>
        )}
      </div>
    </div>
  );
};
